import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { PopoverModule } from 'primeng/popover';
import { SharedModule } from '@shared/shared.module';
import {
  CharterProvenance,
  canOpenProvenance,
  hasProvenance,
  isLaterThanBase,
} from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';
import { evidenceShortLabel } from '@features/dashboard/company/corporate-charter/corporate-charter.util';
import { CharterDocumentViewerService } from '@features/dashboard/company/corporate-charter/services/charter-document-viewer.service';

/**
 * Procedência de um valor do contrato social. Três formas (`variant`):
 * - `icon` (cartões do vigente): só um ícone de documento, que aparece no hover/foco da linha marcada com
 *   `.prov-row` (sempre visível em tela de toque). Se o valor veio de um ato diferente da fonte do cartão
 *   (`base`), vira o selo destacado "alterado na <ato>".
 * - `source` (cabeçalho do cartão): "Fonte: <ato> · <data> · abrir".
 * - `chip` (históricos e linha do tempo): o rótulo curto "alt. 7, p. 2".
 *
 * No clique abre um popover com o
 * documento, a data do ato, a página e o trecho literal, e o botão **Abrir documento na página**, que abre o
 * visualizador do Data Room no documento e na página (via `CharterDocumentViewerService`, provido pela página).
 *
 * Aceita qualquer DTO com os campos de procedência (`CharterValueDto`, `CharterSourceDto`, administrador, regra de
 * assinatura, cláusula, mudança).
 */
@Component({
  selector: 'app-charter-provenance',
  imports: [SharedModule, PopoverModule],
  templateUrl: './charter-provenance.component.html',
  styleUrl: './charter-provenance.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterProvenanceComponent {
  private readonly viewer = inject(CharterDocumentViewerService, { optional: true });

  readonly from = input<CharterProvenance | null | undefined>(null);
  /** Rótulo explícito (ex.: "Cláusula 8ª, alt. 7, p. 4"); sem ele, monta "alt. 7, p. 2". */
  readonly text = input<string | null>(null);
  /** O valor é o vigente: o popover diz "Vigente desde <ato>". */
  readonly current = input(false);
  readonly variant = input<'chip' | 'icon' | 'source'>('chip');
  /** Fonte do cartão: valor de ato posterior a ela mostra o selo "alterado na <ato>". */
  readonly base = input<CharterProvenance | null | undefined>(null);

  /** Veio de um ato posterior à fonte do cartão. */
  protected readonly changed = computed(
    () => this.variant() === 'icon' && isLaterThanBase(this.from(), this.base())
  );

  protected readonly changedLabel = computed(() => {
    const label = this.from()?.actLabel;
    if (!label) {
      return 'alterado em ato posterior';
    }
    // "alterado na 7ª alteração"; nos demais atos ("Estatuto social (AGE ...)"), "alterado em".
    return /^\d+ª/.test(label) ? `alterado na ${label}` : `alterado em ${label}`;
  });

  protected readonly visible = computed(() => hasProvenance(this.from()));

  protected readonly label = computed(() => {
    const from = this.from();
    return (
      this.text() ||
      evidenceShortLabel(from?.actLabel, from?.page) ||
      from?.documentName ||
      'origem'
    );
  });

  protected readonly canOpen = computed(() => !!this.viewer && canOpenProvenance(this.from()));

  protected readonly isUser = computed(() => this.from()?.source === 'user');

  /** "abrir" do cabeçalho: abre direto o documento da fonte. */
  protected openSource(): void {
    const from = this.from();
    if (from && this.viewer) {
      this.viewer.open(from);
    }
  }

  protected openDocument(popover: { hide: () => void }): void {
    const from = this.from();
    if (from && this.viewer) {
      popover.hide();
      this.viewer.open(from);
    }
  }
}
